import React, { useCallback, useEffect, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchWholesaleSales,
  fetchWholesaleCustomers,
  createWholesaleSale,
  selectWholesaleSalesList,
  selectWholesaleSalesListStatus,
  selectWholesaleSalesListError,
  selectWholesaleCustomers,
  selectWholesaleCustomersStatus,
} from "../features/wholesale/wholesaleSlice";
import {
  fetchStores,
  selectStores,
  selectStoresStatus,
} from "../features/onlineSeller/onlineSellerSlice";

const STATUS_OPTIONS = [
  { value: "", label: "Все статусы" },
  { value: "draft", label: "Черновик" },
  { value: "confirmed", label: "Подтверждена" },
  { value: "canceled", label: "Отменена" },
];

const STATUS_LABEL = {
  draft: { text: "Черновик", color: "var(--c-text-muted)" },
  confirmed: { text: "Подтверждена", color: "var(--c-success)" },
  canceled: { text: "Отменена", color: "var(--c-danger)" },
};

const PAYMENT_STATUS_LABEL = {
  unpaid: { text: "Не оплачено", color: "var(--c-danger)" },
  partial: { text: "Частично", color: "var(--c-accent)" },
  paid: { text: "Оплачено", color: "var(--c-success)" },
};

export default function WholesaleSalesListPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sales = useSelector(selectWholesaleSalesList);
  const status = useSelector(selectWholesaleSalesListStatus);
  const error = useSelector(selectWholesaleSalesListError);
  const customers = useSelector(selectWholesaleCustomers);
  const customersStatus = useSelector(selectWholesaleCustomersStatus);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [newSaleModal, setNewSaleModal] = useState(false);
  const [saleCustomerId, setSaleCustomerId] = useState("");
  const [saleStoreId, setSaleStoreId] = useState("");
  const [saleNotes, setSaleNotes] = useState("");
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleError, setSaleError] = useState(null);

  const load = useCallback(() => {
    dispatch(fetchWholesaleSales({ search, status: filterStatus }));
  }, [dispatch, search, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenNewSale = () => {
    if (customersStatus === "idle") dispatch(fetchWholesaleCustomers());
    if (storesStatus === "idle") dispatch(fetchStores());
    setSaleCustomerId("");
    setSaleStoreId("");
    setSaleNotes("");
    setSaleError(null);
    setNewSaleModal(true);
  };

  const handleCreateSale = async () => {
    if (!saleCustomerId) { setSaleError("Выберите клиента"); return; }
    if (!saleStoreId) { setSaleError("Выберите склад"); return; }
    setSaleLoading(true);
    setSaleError(null);
    try {
      const sale = await dispatch(
        createWholesaleSale({
          customer_id: Number(saleCustomerId),
          store_id: Number(saleStoreId),
          notes: saleNotes,
        })
      ).unwrap();
      setNewSaleModal(false);
      navigate(`/wholesale/sales/${sale.id}`);
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка создания продажи";
      setSaleError(msg);
    } finally {
      setSaleLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Оптовые продажи</div>
          <div style={styles.subtitle}>Список всех оптовых продаж</div>
        </div>
        <button type="button" onClick={handleOpenNewSale} style={styles.createBtn}>
          + Новая продажа
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по клиенту..."
          style={styles.searchInput}
        />
        <Select
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            ...STATUS_OPTIONS.map((o) => ({ value: String(o.value), label: o.label })),
          ]}
        />
        <button type="button" onClick={load} style={styles.refreshBtn}>
          Обновить
        </button>
      </div>

      {status === "loading" && <Loading text="Загрузка…" />}
      {status === "failed" && error && <div style={styles.errorBox}>{error}</div>}

      {status === "succeeded" && sales.length === 0 && (
        <div style={styles.empty}>Продажи не найдены. Создайте первую продажу.</div>
      )}

      {sales.length > 0 && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Клиент</th>
                <th style={styles.th}>Дата</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Оплата</th>
                <th style={styles.th}>Итого</th>
                <th style={styles.th}>Долг</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => {
                const st = STATUS_LABEL[sale.status] || { text: sale.status, color: "var(--c-text-muted)" };
                const ps = PAYMENT_STATUS_LABEL[sale.payment_status] || { text: sale.payment_status, color: "var(--c-text-muted)" };
                const debt = Number(sale.debt_amount) || 0;
                const customerName =
                  typeof sale.customer === "object"
                    ? sale.customer?.name
                    : sale.customer_name || `Клиент #${sale.customer_id}`;
                return (
                  <tr
                    key={sale.id}
                    style={styles.tr}
                    onClick={() => navigate(`/wholesale/sales/${sale.id}`)}
                  >
                    <td style={{ ...styles.td, fontFamily: "monospace", opacity: 0.7 }}>
                      #{sale.id}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.customerName}>{customerName}</div>
                    </td>
                    <td style={styles.td}>
                      {sale.sale_dt
                        ? new Date(sale.sale_dt).toLocaleDateString("ru-RU", {
                            day: "2-digit", month: "2-digit", year: "numeric",
                          })
                        : "—"}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: st.color, borderColor: st.color + "55", background: st.color + "18" }}>
                        {st.text}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: ps.color, borderColor: ps.color + "55", background: ps.color + "18" }}>
                        {ps.text}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontWeight: 900 }}>
                      {Number(sale.grand_total || 0).toFixed(2)} сом
                    </td>
                    <td style={styles.td}>
                      {debt > 0 ? (
                        <span style={styles.debtBadge}>{debt.toFixed(2)} сом</span>
                      ) : (
                        <span style={styles.paidBadge}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {newSaleModal && (
        <div style={styles.overlay} onMouseDown={() => !saleLoading && setNewSaleModal(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Новая оптовая продажа</div>
              <button
                type="button"
                onClick={() => !saleLoading && setNewSaleModal(false)}
                style={styles.modalClose}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {saleError && <div style={styles.errorBox}>{saleError}</div>}

              <div style={styles.field}>
                <label style={styles.label}>Клиент *</label>
                {customersStatus === "loading" && (
                  <Loading inline text="Загрузка клиентов…" />
                )}
                <Select
                  value={saleCustomerId}
                  onChange={setSaleCustomerId}
                  options={[
                    { value: "", label: "— Выберите клиента —" },
                    ...customers.map((c) => ({ value: String(c.id), label: c.name })),
                  ]}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Склад *</label>
                {storesStatus === "loading" && (
                  <Loading inline text="Загрузка складов…" />
                )}
                <Select
                  value={saleStoreId}
                  onChange={setSaleStoreId}
                  options={[
                    { value: "", label: "— Выберите склад —" },
                    ...stores.map((s) => ({
                      value: String(s.id),
                      label: s.name || `Склад #${s.id}`,
                    })),
                  ]}
                  isSearchable={false}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Заметки</label>
                <textarea
                  value={saleNotes}
                  onChange={(e) => setSaleNotes(e.target.value)}
                  placeholder="Необязательно"
                  style={styles.textarea}
                  rows={2}
                />
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.confirmBtn}
                  onClick={handleCreateSale}
                  disabled={saleLoading}
                >
                  {saleLoading ? "Создание..." : "Создать и перейти →"}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => setNewSaleModal(false)}
                  disabled={saleLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  header: { padding: "16px 24px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", background: "var(--c-bg)", flexShrink: 0 },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subtitle: { fontSize: 15, color: "var(--c-text-muted)", marginTop: 3 },
  createBtn: { padding: "9px 18px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 },
  toolbar: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "12px 24px", borderBottom: "1px solid var(--c-border)", background: "var(--c-bg)", flexShrink: 0 },
  searchInput: { flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  select: { padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15, cursor: "pointer" },
  refreshBtn: { padding: "8px 16px", borderRadius: 99, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15, whiteSpace: "nowrap" },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  errorBox: { margin: "0 24px", padding: "12px 16px", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 16, color: "var(--c-danger)", fontSize: 15 },
  empty: { padding: "48px 24px", textAlign: "center", fontSize: 16, color: "var(--c-text-muted)" },
  tableWrap: { margin: "0 24px 24px", border: "1px solid var(--c-border)", borderRadius: 16, overflow: "hidden", background: "var(--c-card)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--c-text-sub)", background: "var(--c-input)", borderBottom: "1px solid var(--c-border)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.4px" },
  tr: { cursor: "pointer" },
  td: { padding: "11px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", verticalAlign: "middle", color: "var(--c-text)" },
  customerName: { fontWeight: 700, color: "var(--c-text)" },
  badge: { display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 99, fontSize: 13, fontWeight: 700, border: "1px solid", whiteSpace: "nowrap" },
  debtBadge: { display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 99, background: "rgba(248,113,113,0.14)", border: "1px solid rgba(248,113,113,0.2)", color: "var(--c-danger)", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" },
  paidBadge: { color: "var(--c-text-muted)", fontSize: 14 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 },
  modal: { width: "min(460px, 90vw)", maxHeight: "90vh", background: "var(--c-card)", border: "1px solid var(--c-border-light)", borderRadius: 16, boxShadow: "0 24px 64px rgba(0,0,0,0.6)", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { padding: "14px 20px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "var(--c-text)" },
  modalClose: { width: 30, height: 30, borderRadius: 16, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center" },
  modalBody: { padding: 20, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--c-text-muted)" },
  hint: { fontSize: 13, color: "var(--c-text-muted)" },
  input: { padding: "9px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15, fontFamily: "inherit", cursor: "pointer" },
  textarea: { padding: "9px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15, resize: "vertical", fontFamily: "inherit" },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: { padding: "9px 20px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 15 },
  cancelBtn: { padding: "9px 20px", borderRadius: 99, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15 },
};
